import { LANGUAGES } from "./languages";

/** The language the copy is authored in, and where everything else falls back to. */
export const DEFAULT_LOCALE = "pt-BR";

/** The tags this application ships, in declaration order. */
export const SUPPORTED_LOCALES = LANGUAGES.map((language) => language.code);

/** Where the choice is stored, shared across every Ingresse application. */
export const LOCALE_COOKIE = "locale";

/**
 * Canonicalizes the many spellings the platform emits into a tag this app ships.
 *
 * WHY THIS EXISTS. The locale cookie is written by several applications, and not
 * all of them agree on the spelling: the Java side of the platform emits the
 * gettext form (`pt_PT`), hosts sometimes pass the bare language (`pt`), and a
 * careless producer sends `pt-pt`. Before this, the raw cookie value went
 * straight into i18next as `lng`; anything but an exact match found no resource
 * and silently rendered pt-BR. That is invisible to the visitor — they just read
 * the wrong Portuguese.
 *
 * Resolution order, and the order matters:
 *
 *   1. exact match on the canonical tag (`pt-PT` → `pt-PT`);
 *   2. match on the language subtag against the declared list, first wins.
 *
 * Step 2 is why `pt` resolves to pt-BR rather than pt-PT: pt-BR is declared
 * first in `languages.js`, and it is the larger audience. Reordering that list
 * silently changes which variant a bare `pt` lands on — it is load-bearing.
 *
 * Returns null when nothing matches, so the caller decides the fallback rather
 * than having one imposed here.
 *
 * @param {string|null|undefined} tag
 * @returns {string|null}
 */
export function normalizeLocale(tag) {
    if (typeof tag !== "string") return null;

    const trimmed = tag.trim().replace(/_/g, "-");
    if (!trimmed) return null;

    const [language, region] = trimmed.split("-");
    const canonical = region
        ? `${language.toLowerCase()}-${region.toUpperCase()}`
        : language.toLowerCase();

    const exact = SUPPORTED_LOCALES.find((locale) => locale === canonical);
    if (exact) return exact;

    return (
        SUPPORTED_LOCALES.find(
            (locale) => locale.split("-")[0] === canonical.split("-")[0],
        ) || null
    );
}

/**
 * The raw value of the shared `locale` cookie, or null.
 *
 * Reads the LAST match rather than the first. Two cookies with the same name can
 * coexist — one host-only, one scoped to `.ingresse.com` — and `document.cookie`
 * exposes no way to tell them apart: the header carries names and values only.
 *
 * RFC 6265 §5.4 sorts that header by descending path length and, for equal
 * paths, by ascending creation time. Both of ours live at `path=/`, so the last
 * entry is simply the most RECENTLY created one. That is the tie-break we want
 * — the freshest write wins — but it is worth naming precisely, because reading
 * the first match (which this module used to do) means preferring the oldest,
 * which is how a stale host-only copy kept overriding the shared preference.
 *
 * @returns {string|null}
 */
export function readLocaleCookie() {
    if (typeof document === "undefined") return null;

    const prefix = `${LOCALE_COOKIE}=`;
    const matches = document.cookie
        .split(";")
        .map((cookie) => cookie.trim())
        .filter((cookie) => cookie.startsWith(prefix))
        .map((cookie) => cookie.slice(prefix.length));

    return matches.length ? matches[matches.length - 1] : null;
}

/**
 * The `locale` query parameter, or null.
 *
 * Other Ingresse applications hand the language over on the URL when they send
 * someone here — the AWA authenticator echoes back whatever was picked on the
 * login screen. Without this the parameter was simply ignored, and a visitor
 * arriving from a European Portuguese page landed on Brazilian Portuguese.
 *
 * @returns {string|null}
 */
export function readLocaleQuery() {
    if (typeof window === "undefined") return null;

    try {
        return new URLSearchParams(window.location.search).get(LOCALE_COOKIE);
    } catch {
        // A malformed query string is not a preference.
        return null;
    }
}

/**
 * Persists the choice on the registrable domain, so it survives the jump
 * between Ingresse applications.
 *
 * `secure` and `SameSite=Lax` are NOT decoration. The AWA applications write
 * this same cookie through `@ingresse/awa-sdk-web` with both attributes, and a
 * write here without them would replace their cookie and strip the policy —
 * `Lax` is precisely what lets it survive the cross-site navigation this epic
 * is built on. Writing `Strict` (or omitting it, which browsers increasingly
 * treat as `Lax` but inconsistently) would break sharing in one direction only,
 * which is the kind of bug that shows up as "the language resets sometimes".
 *
 * @param {string} locale a tag already normalized by `normalizeLocale`
 */
export function writeLocaleCookie(locale) {
    if (typeof document === "undefined") return;

    const attributes = "domain=.ingresse.com;path=/;max-age=31536000";
    const isSecureContext =
        typeof window !== "undefined" && window.location.protocol === "https:";

    // `secure` is refused over plain http, which is how the dev server runs on
    // some machines; dropping it there is the difference between a cookie and
    // no cookie at all.
    document.cookie = isSecureContext
        ? `${LOCALE_COOKIE}=${locale};${attributes};secure;samesite=lax`
        : `${LOCALE_COOKIE}=${locale};${attributes};samesite=lax`;
}

/**
 * The locale this session should render in.
 *
 * The stored preference OUTRANKS the query parameter, deliberately. The
 * parameter is a hint from whichever application sent the visitor here; the
 * cookie is a choice they made themselves, in this or another Ingresse
 * application. A host passing `?locale=` should not silently undo it.
 *
 * The same precedence is what `createLocaleManager({ prefer: "stored" })` gives
 * the AWA applications, so the platform behaves the same way on both sides.
 *
 * @returns {string} always a tag this app ships
 */
export function resolveLocale() {
    return (
        normalizeLocale(readLocaleCookie()) ||
        normalizeLocale(readLocaleQuery()) ||
        DEFAULT_LOCALE
    );
}
